import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { api } from "./lib/api";
import { useAuth, AuthProvider, ProtectedRoute } from "./lib/auth";
import { CATEGORY_NAMES, POKER_GLOSSARY, GTO_CHARTS, TABLE_POSITIONS, TABLE_COORDS, POSITION_MAP, parsePositionActions, resolveHeroSeat, resolveVillainSeats } from "./lib/constants";
import { PlayingCard, PositionNode, GlossaryTag, AdBanner, AdSlot } from "./components/shared";
import { Header } from "./components/Header";
import {
  Spade, LayoutDashboard, GraduationCap, BarChart3, User, LogOut, Crown,
  ChevronRight, Target, Flame, Trophy, Brain, Layers, DollarSign, Compass,
  BarChart, ArrowRight, Check, Loader2, Lock, Sparkles, MessageSquare,
  Plus, Trash2, X, Calendar, Medal, CreditCard, Zap
} from "lucide-react";

// ============ Auth Callback ============
const AuthCallback = () => {
  const navigate = useNavigate();
  const { login, setLoading } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const processAuth = async () => {
      const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
      if (!sessionId) { 
        toast.error("Authentication failed"); 
        setLoading(false);
        navigate("/login"); 
        return; 
      }
      try {
        const response = await api.post("/auth/session", { session_id: sessionId });
        if (response.data.session_token) localStorage.setItem("session_token", response.data.session_token);
        // Clear the hash from URL before navigating
        window.history.replaceState(null, "", window.location.pathname);
        login(response.data.user);
        navigate(response.data.user.onboarding_completed ? "/dashboard" : "/onboarding", { replace: true });
      } catch {
        toast.error("Authentication failed");
        setLoading(false);
        // Clear the hash so we don't loop
        window.history.replaceState(null, "", "/login");
        navigate("/login", { replace: true });
      }
    };
    processAuth();
  }, [navigate, login, setLoading]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mx-auto mb-4" />
        <p className="text-neutral-400">Signing you in...</p>
      </div>
    </div>
  );
};

// ============ Landing Page ============
const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const handleGetStarted = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { icon: Target, title: "Scenario Training", description: "Practice real poker decisions with hundreds of curated scenarios across all game types." },
    { icon: Brain, title: "AI Coaching", description: "Get instant explanations tailored to your skill level from our AI poker coach." },
    { icon: BarChart3, title: "Progress Tracking", description: "Track your accuracy, identify weak spots, and watch your game improve over time." },
    { icon: Layers, title: "Multiple Categories", description: "Preflop, postflop, cash games, tournaments, and more specialized training modules." },
    { icon: Flame, title: "Streak System", description: "Stay motivated with daily streaks and achievement tracking." },
    { icon: Trophy, title: "Leaderboards", description: "Compete with other players and climb the rankings." },
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/5f6ae614-3edb-4cc6-acfa-5d2e3037961b/images/59fdcba519c33d391a944d6cc452d95aefd4d8a242dc34747667459023e8196b.png')` }} />
        <div className="absolute inset-0 bg-black/70" />
        <Header />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter mb-6" data-testid="hero-title">Master Poker<span className="text-[#D4AF37]"> One Decision </span>at a Time</h1>
            <p className="text-lg sm:text-xl text-neutral-300 mb-8 leading-relaxed">Train with scenario-based drills, track your progress, and get AI-powered coaching to transform your poker game.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={handleGetStarted} className="bg-[#D4AF37] text-black font-semibold rounded-md px-8 py-4 text-lg hover:bg-[#B3922A] transition-colors gold-glow flex items-center justify-center gap-2" data-testid="get-started-btn">Start Training Free <ArrowRight className="w-5 h-5" /></button>
              <Link to="/login" className="border border-white/20 text-white font-semibold rounded-md px-8 py-4 text-lg hover:bg-white/5 transition-colors flex items-center justify-center" data-testid="learn-more-btn">Learn More</Link>
            </div>
          </div>
        </div>
      </div>
      <div className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight mb-4">Everything You Need to Level Up</h2>
            <p className="text-neutral-400 max-w-2xl mx-auto">Comprehensive training tools designed by poker professionals</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-[#121212] border border-white/5 rounded-lg p-8 hover:border-white/10 transition-colors">
                <f.icon className="w-10 h-10 text-[#D4AF37] mb-4" /><h3 className="text-xl font-medium mb-2">{f.title}</h3><p className="text-neutral-400">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight mb-4">Simple, Transparent Pricing</h2>
            <p className="text-neutral-400">Start free, upgrade when you're ready</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-[#121212] border border-white/5 rounded-lg p-8">
              <h3 className="text-xl font-medium mb-2">Free</h3>
              <p className="text-4xl font-semibold mb-6">$0<span className="text-lg text-neutral-400">/forever</span></p>
              <ul className="space-y-3 mb-8">{["All 105+ training scenarios","All 6 training modules","3 AI coaching requests/day","Progress tracking & leaderboards","Daily challenges","Ad-supported"].map((f) => <li key={f} className="flex items-center gap-2 text-neutral-300"><Check className="w-4 h-4 text-[#10B981]" />{f}</li>)}</ul>
              <button onClick={handleGetStarted} className="w-full border border-white/20 text-white font-semibold rounded-md px-6 py-3 hover:bg-white/5 transition-colors" data-testid="free-plan-btn">Get Started Free</button>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-lg p-8">
              <h3 className="text-xl font-medium mb-2">Ad-Free Monthly</h3>
              <p className="text-4xl font-semibold mb-6">$10<span className="text-lg text-neutral-400">/month</span></p>
              <ul className="space-y-3 mb-8">{["Everything in Free","No advertisements","Unlimited AI coaching","Clean distraction-free training","Cancel anytime"].map((f) => <li key={f} className="flex items-center gap-2 text-neutral-300"><Check className="w-4 h-4 text-[#D4AF37]" />{f}</li>)}</ul>
              <button onClick={handleGetStarted} className="w-full bg-white/10 text-white font-semibold rounded-md px-6 py-3 hover:bg-white/20 transition-colors">Subscribe Monthly</button>
            </div>
            <div className="bg-gradient-to-br from-[#121212] to-[#0A0A0A] border border-[#D4AF37]/20 rounded-lg p-8 relative">
              <div className="absolute -top-3 right-6 px-3 py-1 premium-gradient rounded-full text-black text-xs font-semibold">BEST VALUE</div>
              <h3 className="text-xl font-medium mb-2">Ad-Free Lifetime</h3>
              <p className="text-4xl font-semibold mb-6">$70<span className="text-lg text-neutral-400"> once</span></p>
              <ul className="space-y-3 mb-8">{["Everything in Free","No advertisements forever","Unlimited AI coaching","One-time payment","Best value over time"].map((f) => <li key={f} className="flex items-center gap-2 text-neutral-300"><Check className="w-4 h-4 text-[#D4AF37]" />{f}</li>)}</ul>
              <button onClick={handleGetStarted} className="w-full bg-[#D4AF37] text-black font-semibold rounded-md px-6 py-3 hover:bg-[#B3922A] transition-colors gold-glow" data-testid="premium-plan-btn">Buy Lifetime Access</button>
            </div>
          </div>
        </div>
      </div>
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-md bg-[#D4AF37] flex items-center justify-center"><Spade className="w-5 h-5 text-black" /></div><span className="font-semibold">PokerMentor</span></div>
            <p className="text-neutral-400 text-sm">&copy; 2026 PokerMentor. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ============ Login Page ============
const LoginPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6"><div className="w-10 h-10 rounded-md bg-[#D4AF37] flex items-center justify-center"><Spade className="w-6 h-6 text-black" /></div><span className="font-semibold text-xl">PokerMentor</span></Link>
          <h1 className="text-2xl sm:text-3xl font-semibold mb-2">Welcome back</h1>
          <p className="text-neutral-400">Sign in to continue your training</p>
        </div>
        <div className="bg-[#121212] border border-white/5 rounded-lg p-6 sm:p-8">
          <button onClick={handleGoogleLogin} className="w-full bg-white text-black font-semibold rounded-md px-6 py-3 hover:bg-neutral-200 transition-colors flex items-center justify-center gap-3" data-testid="google-login-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>
          <div className="mt-6 text-center text-sm text-neutral-400">By continuing, you agree to our Terms of Service and Privacy Policy</div>
        </div>
      </div>
    </div>
  );
};

// ============ Onboarding Page ============
const OnboardingPage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [loading, setLoading] = useState(false);
  const levels = [
    { id: "beginner", title: "Beginner", description: "I'm new to poker or still learning the basics", icon: GraduationCap },
    { id: "intermediate", title: "Intermediate", description: "I understand fundamentals and want to improve my strategy", icon: Target },
    { id: "advanced", title: "Advanced", description: "I'm experienced and looking to refine my edge", icon: Trophy }
  ];
  const handleContinue = async () => {
    if (!selectedLevel) return;
    setLoading(true);
    try {
      await api.post("/auth/onboarding", { experience_level: selectedLevel });
      await checkAuth();
      navigate("/dashboard");
    } catch { toast.error("Failed to save preferences"); } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12">
      <div className="max-w-xl w-full">
        <div className="text-center mb-8"><h1 className="text-2xl sm:text-3xl font-semibold mb-2">Welcome, {user?.name?.split(" ")[0]}!</h1><p className="text-neutral-400">Let's personalize your training experience</p></div>
        <div className="bg-[#121212] border border-white/5 rounded-lg p-6 sm:p-8">
          <h2 className="text-lg font-medium mb-4">What's your poker experience level?</h2>
          <div className="space-y-3">
            {levels.map((level) => (
              <button key={level.id} onClick={() => setSelectedLevel(level.id)} className={`w-full flex items-center gap-4 p-4 rounded-lg border transition-all ${selectedLevel === level.id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 hover:border-white/20"}`} data-testid={`level-${level.id}`}>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${selectedLevel === level.id ? "bg-[#D4AF37]/20" : "bg-white/5"}`}><level.icon className={`w-6 h-6 ${selectedLevel === level.id ? "text-[#D4AF37]" : "text-neutral-400"}`} /></div>
                <div className="text-left flex-1"><div className="font-medium">{level.title}</div><div className="text-sm text-neutral-400">{level.description}</div></div>
                {selectedLevel === level.id && <Check className="w-5 h-5 text-[#D4AF37]" />}
              </button>
            ))}
          </div>
          <button onClick={handleContinue} disabled={!selectedLevel || loading} className="w-full mt-6 bg-[#D4AF37] text-black font-semibold rounded-md px-6 py-3 hover:bg-[#B3922A] transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="onboarding-continue-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continue <ArrowRight className="w-5 h-5" /></>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ Dashboard Page ============
const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [weakAreas, setWeakAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [s, r, w] = await Promise.all([api.get("/stats/me"), api.get("/stats/recent-sessions"), api.get("/stats/weak-areas")]);
        if (!cancelled) { setStats(s.data); setRecentSessions(r.data); setWeakAreas(w.data); }
      } catch { /* silent */ } finally { if (!cancelled) setLoading(false); }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="min-h-screen bg-[#050505]"><Header /><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" /></div></div>;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="dashboard-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8"><h1 className="text-2xl sm:text-3xl font-semibold mb-2">Welcome back, {user?.name?.split(" ")[0]}!</h1><p className="text-neutral-400">Ready to sharpen your skills?</p></div>
        <AdSlot size="banner" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Link to="/training" className="bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-lg p-6 hover:border-[#D4AF37]/40 transition-all group" data-testid="start-training-btn"><div className="flex items-center justify-between"><div><h3 className="text-lg font-medium mb-1">Start Training</h3><p className="text-sm text-neutral-400">Practice poker scenarios</p></div><ArrowRight className="w-6 h-6 text-[#D4AF37] group-hover:translate-x-1 transition-transform" /></div></Link>
          <Link to="/daily-challenge" className="bg-gradient-to-br from-[#10B981]/10 to-transparent border border-[#10B981]/20 rounded-lg p-6 hover:border-[#10B981]/40 transition-all group" data-testid="daily-challenge-btn"><div className="flex items-center justify-between"><div><h3 className="text-lg font-medium mb-1">Daily Challenge</h3><p className="text-sm text-neutral-400">Today's featured scenario</p></div><Calendar className="w-6 h-6 text-[#10B981] group-hover:scale-110 transition-transform" /></div></Link>
          <Link to="/leaderboard" className="bg-[#121212] border border-white/5 rounded-lg p-6 hover:border-white/10 transition-all group" data-testid="leaderboard-btn"><div className="flex items-center justify-between"><div><h3 className="text-lg font-medium mb-1">Leaderboard</h3><p className="text-sm text-neutral-400">See how you rank</p></div><Trophy className="w-6 h-6 text-neutral-400 group-hover:text-[#D4AF37] transition-colors" /></div></Link>
        </div>
        <AdBanner />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 mt-6">
          <div className="bg-[#121212] border border-white/5 rounded-lg p-6"><div className="text-sm text-neutral-400 mb-1">Completed</div><div className="text-3xl font-semibold font-mono">{stats?.total_completed || 0}</div></div>
          <div className="bg-[#121212] border border-white/5 rounded-lg p-6"><div className="text-sm text-neutral-400 mb-1">Accuracy</div><div className="text-3xl font-semibold font-mono text-[#10B981]">{stats?.accuracy || 0}%</div></div>
          <div className="bg-[#121212] border border-white/5 rounded-lg p-6"><div className="text-sm text-neutral-400 mb-1">Current Streak</div><div className="text-3xl font-semibold font-mono flex items-center gap-2">{stats?.current_streak || 0}{stats?.current_streak > 0 && <Flame className="w-6 h-6 text-orange-500" />}</div></div>
          <div className="bg-[#121212] border border-white/5 rounded-lg p-6"><div className="text-sm text-neutral-400 mb-1">Best Streak</div><div className="text-3xl font-semibold font-mono">{stats?.longest_streak || 0}</div></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-[#121212] border border-white/5 rounded-lg p-6"><h2 className="text-lg font-medium mb-4">Recent Sessions</h2>{recentSessions.length > 0 ? <div className="space-y-3">{recentSessions.map((s) => <div key={s.date} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"><div className="text-sm text-neutral-400">{s.date}</div><div className="flex items-center gap-4"><span className="text-sm">{s.total} scenarios</span><span className={`text-sm font-mono ${s.accuracy >= 70 ? "text-[#10B981]" : "text-[#E11D48]"}`}>{s.accuracy}%</span></div></div>)}</div> : <p className="text-neutral-400 text-sm">No sessions yet. Start training to see your progress!</p>}</div>
          <div className="bg-[#121212] border border-white/5 rounded-lg p-6"><h2 className="text-lg font-medium mb-4">Areas to Improve</h2>{weakAreas.length > 0 ? <div className="space-y-3">{weakAreas.map((a) => <div key={a.category} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"><div className="text-sm">{CATEGORY_NAMES[a.category] || a.category}</div><div className="flex items-center gap-4"><span className="text-sm text-neutral-400">{a.completed} tries</span><span className="text-sm font-mono text-[#E11D48]">{a.accuracy}%</span></div></div>)}</div> : <p className="text-neutral-400 text-sm">Complete more scenarios to identify areas for improvement!</p>}</div>
        </div>
        {!user?.is_premium && (
          <div className="mt-8 bg-[#121212] border border-[#D4AF37]/20 rounded-lg p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div><h3 className="text-lg font-medium mb-1 flex items-center gap-2"><Crown className="w-5 h-5 text-[#D4AF37]" />Go Ad-Free</h3><p className="text-neutral-400 text-sm">Remove ads for a cleaner training experience. $10/mo or $70 lifetime.</p></div>
              <Link to="/profile" className="bg-[#D4AF37] text-black font-semibold rounded-md px-6 py-3 hover:bg-[#B3922A] transition-colors whitespace-nowrap" data-testid="upgrade-premium-btn">Remove Ads</Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ============ Remaining pages imported lazily for brevity - kept inline but refactored ============
// Training, Scenario, Progress, Profile, Admin, Leaderboard, DailyChallenge, Education, PaymentSuccess
// All pages follow same pattern: removed console.*, fixed deps, stable keys

// Due to file size constraints, remaining pages stay in App.js but with all fixes applied
// In a production codebase, each would be its own file under src/pages/

// ============ Import remaining pages from separate file ============
import { TrainingPage, ScenarioPage, ProgressPage, ProfilePage, AdminPage, LeaderboardPage, DailyChallengePage, EducationPage, PaymentSuccessPage, HandReplayerPage } from "./pages";

// ============ App Router ============
const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Check hash for session_id BEFORE any route matching
  // This handles the OAuth callback from Emergent Auth
  if (window.location.hash?.includes("session_id=")) return <AuthCallback />;
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><TrainingPage /></ProtectedRoute>} />
      <Route path="/scenario" element={<ProtectedRoute><ScenarioPage /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/education" element={<ProtectedRoute><EducationPage /></ProtectedRoute>} />
      <Route path="/daily-challenge" element={<ProtectedRoute><DailyChallengePage /></ProtectedRoute>} />
      <Route path="/hand-replayer" element={<ProtectedRoute><HandReplayerPage /></ProtectedRoute>} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" toastOptions={{ style: { background: "#121212", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" } }} />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}
